import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import PhotoOverlay from 'dibs-interiors-photos/exports/PhotoOverlay';
import PhotoArrowOverlay from 'dibs-interiors-photos/exports/PhotoArrowOverlay';
import { SbRespInteriorsViewButton } from './SbRespInteriorsViewButton';
import { SbSharedInteriorsHeader } from '../SbSharedInteriorsModule/SbSharedInteriorsHeader';
import { SbSharedInteriorsBrowseMore } from '../SbSharedInteriorsModule/SbSharedInteriorsBrowseMore';
import { SbRespInteriorsLazyLoadImage } from './SbRespInteriorsLazyLoadImage';
import { getPhotoIndex } from '../SbSharedInteriorsModule/interiorsModuleHelpers';

import styles from './SbRespInteriorsPhotoGallery.scss';

import { SbRespInteriorsPhotoGallery_interiorPhotos } from './__generated__/SbRespInteriorsPhotoGallery_interiorPhotos.graphql';
import { SbRespInteriorsPhotoGallery_itemSearch } from './__generated__/SbRespInteriorsPhotoGallery_itemSearch.graphql';

type Props = {
    interiorPhotos: SbRespInteriorsPhotoGallery_interiorPhotos | null;
    itemSearch: SbRespInteriorsPhotoGallery_itemSearch;
    photoIndex: number;
    onPhotoIndexChange: (newIndex: number) => void;
    onViewInteriorClick: () => void;
    onPhotoClick: () => void;
};

const SbRespInteriorsPhotoGalleryComponent: FC<Props> = ({
    interiorPhotos,
    itemSearch,
    photoIndex,
    onPhotoIndexChange,
    onViewInteriorClick,
    onPhotoClick,
}) => {
    const photos = interiorPhotos?.photos || [];
    // +1 b/c showing browse screen at the end
    const photoCount = photos.length + 1;
    const lastPhotoIndex = photoCount - 1;

    const handlePageChange = (nextIndex: number): void => {
        onPhotoIndexChange(getPhotoIndex(nextIndex, lastPhotoIndex));
    };

    const isLastPage = photoIndex === lastPhotoIndex;
    const photo = photos[photoIndex];

    return (
        <div className={styles.wrapper}>
            <div className={styles.imgWrapper}>
                {isLastPage ? (
                    <div className={styles.browseMoreWrapper}>
                        <SbSharedInteriorsBrowseMore interiorPhotos={interiorPhotos} />
                    </div>
                ) : (
                    <SbRespInteriorsLazyLoadImage imgClass={styles.img} photo={photo} />
                )}
                {photoCount > 0 && (
                    <div
                        className={styles.overlayWrapper}
                        onClick={onPhotoClick}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                onPhotoClick();
                            }
                        }}
                    >
                        <PhotoOverlay>
                            <PhotoArrowOverlay
                                hideNextArrow={isLastPage}
                                hidePrevArrow={photoIndex === 0}
                                onNextClick={event => {
                                    event.stopPropagation();
                                    handlePageChange(photoIndex + 1);
                                }}
                                onPrevClick={event => {
                                    event.stopPropagation();
                                    handlePageChange(photoIndex - 1);
                                }}
                            />
                        </PhotoOverlay>
                    </div>
                )}
            </div>
            <div className={styles.headerWrapper}>
                <SbSharedInteriorsHeader itemSearch={itemSearch} />
            </div>
            <SbRespInteriorsViewButton onClick={onViewInteriorClick} />
        </div>
    );
};

export const SbRespInteriorsPhotoGallery = createFragmentContainer(
    SbRespInteriorsPhotoGalleryComponent,
    {
        itemSearch: graphql`
            fragment SbRespInteriorsPhotoGallery_itemSearch on ItemSearchQueryConnection {
                ...SbSharedInteriorsHeader_itemSearch
            }
        `,
        interiorPhotos: graphql`
            fragment SbRespInteriorsPhotoGallery_interiorPhotos on SearchBrowseInteriorPhotosModule {
                ...SbSharedInteriorsBrowseMore_interiorPhotos
                ...interiorsModuleHelpers_interiorPhotos
                photos {
                    path
                    name
                }
            }
        `,
    }
);
